/**
 * Voice Guidance Service - Text-to-speech for turn-by-turn navigation
 * Waze-style voice announcements
 */

interface VoiceConfig {
  enabled: boolean;
  volume: number; // 0-1
  rate: number; // 0.1-10, 1 = normal
  pitch: number; // 0-2, 1 = normal
  lang: string; // e.g., 'en-US', 'en-GB'
}

class VoiceGuidanceService {
  private synth: SpeechSynthesis;
  private config: VoiceConfig;
  private isSpeaking: boolean = false;
  private queue: string[] = [];

  constructor() {
    this.synth = window.speechSynthesis;
    this.config = {
      enabled: true,
      volume: 0.8,
      rate: 1.0,
      pitch: 1.0,
      lang: 'en-US',
    };
  }

  /**
   * Speak instruction with natural voice
   */
  speak(text: string, options?: { priority?: 'high' | 'normal' }): void {
    if (!this.config.enabled || !text) return;

    // Cancel current speech if high priority
    if (options?.priority === 'high' && this.isSpeaking) {
      this.synth.cancel();
      this.queue = [];
    }

    // Add to queue if already speaking
    if (this.isSpeaking) {
      this.queue.push(text);
      return;
    }

    this.speakNow(text);
  }

  private speakNow(text: string): void {
    const utterance = new SpeechSynthesisUtterance(text);

    utterance.volume = this.config.volume;
    utterance.rate = this.config.rate;
    utterance.pitch = this.config.pitch;
    utterance.lang = this.config.lang;

    utterance.onstart = () => {
      this.isSpeaking = true;
    };

    utterance.onend = () => {
      this.isSpeaking = false;
      // Process queue
      if (this.queue.length > 0) {
        const next = this.queue.shift();
        if (next) this.speakNow(next);
      }
    };

    utterance.onerror = (event) => {
      console.error('Speech synthesis error:', event);
      this.isSpeaking = false;
    };

    this.synth.speak(utterance);
  }

  /**
   * Announce turn instruction (e.g., "In 500 meters, turn right onto Main Street")
   */
  announceTurn(instruction: string, distance: number): void {
    const distanceText = this.formatDistance(distance);
    const message = `In ${distanceText}, ${instruction}`;
    this.speak(message);
  }

  /**
   * Announce immediate turn (e.g., "Turn right now")
   */
  announceImmediateTurn(instruction: string): void {
    this.speak(instruction, { priority: 'high' });
  }

  /**
   * Announce arrival
   */
  announceArrival(destinationName?: string): void {
    const message = destinationName
      ? `You have arrived at ${destinationName}`
      : 'You have arrived at your destination';
    this.speak(message, { priority: 'high' });
  }

  /**
   * Announce rerouting
   */
  announceReroute(): void {
    this.speak('Recalculating route', { priority: 'high' });
  }

  /**
   * Format distance for speech
   */
  private formatDistance(meters: number): string {
    if (meters < 50) {
      return `${Math.round(meters / 10) * 10} meters`;
    } else if (meters < 1000) {
      return `${Math.round(meters / 50) * 50} meters`;
    } else {
      const km = (meters / 1000).toFixed(1);
      return `${km} kilometers`;
    }
  }

  /**
   * Enable/disable voice guidance
   */
  setEnabled(enabled: boolean): void {
    this.config.enabled = enabled;
    if (!enabled) {
      this.synth.cancel();
      this.queue = [];
    }
  }

  /**
   * Set voice volume (0-1)
   */
  setVolume(volume: number): void {
    this.config.volume = Math.max(0, Math.min(1, volume));
  }

  /**
   * Set speech rate (0.1-10, 1 = normal)
   */
  setRate(rate: number): void {
    this.config.rate = Math.max(0.1, Math.min(10, rate));
  }

  /**
   * Stop all speech
   */
  stop(): void {
    this.synth.cancel();
    this.queue = [];
    this.isSpeaking = false;
  }

  /**
   * Check if speech synthesis is supported
   */
  isSupported(): boolean {
    return 'speechSynthesis' in window;
  }
}

// Export singleton instance
export const voiceGuidance = new VoiceGuidanceService();
